/**
 * 波A · 长图模板 `schema_json` 的**唯一**出入口(设计方案 §3.7 B1;v2 2026-08-17)。
 *
 * ═══ 存什么:所见即所得 payload(v2)═══════════════════════════════════════════
 * 以 `LongImageCreatePayload` 为形,**保留 admin 示例内容+示例图,保留结构与视觉**:
 *   - canvas 全保留(方向/宽度/清晰度/背景/用途/横宽版式);★清晰度套用时不沿用、一律从 1K 起(1006,见 useLongImageBuilder scale 初值);
 *   - style(L1 画风)与 styleTokens(L2 配色)全保留 —— 模板的价值一半在配色;
 *   - blocks 结构 + **示例内容**全保留(逐原语类型清洗,见下)。
 * **仍不进 schema**:provider(租户能力不同,套用时按租户解算)、blueprintId/formulaName
 * (那是某次追爆的运行痕迹)—— 顶层白名单继续挡。
 *
 * ═══ v1→v2 反转 ═══════════════════════════════════════════════════════════════
 * v1 刻意**裁空内容**(image prompt→占位、refKey/objectKey 删、text/data→defaultPropsFor 占位),
 * 理由是"素材/文案是租户的"。老板前提①(模板全 admin 上传、无租户数据边界)推翻它:
 * 示例内容/图片**直接进 schema**,套用后中间数据+右画布即所见即所得。P2 里 admin 块图改落
 * **平台桶**、模板读接口对 schema 里的图 key **读时重签**(素材从此是平台的,可长期跨租户访问)。
 *
 * ═══ 两侧仍都清洗(改"裁内容"为"类型安全保留")═══════════════════════════════
 * `buildTemplateSchema`(admin 存)/`parseTemplateSchema`(租户套用)都过 `sanitizeBlockProps`:
 * **保留原值**,但逐键按期望类型构造 —— 对象伪装夹带(`blockRatio:{refKey:…}`、数组元素里塞 key)
 * 一律剥掉。读侧同样再清一遍:schema_json 是 DB 陈年 JSON(后端透明存取),直连改过的坏行可能进来。
 *
 * ═══ 清洗规则(逐原语)═══════════════════════════════════════════════════════
 * | 原语  | 保留(类型安全)                                              |
 * |------|--------------------------------------------------------------|
 * | image| genMode、blockRatio(ratio 串)、**prompt 真值**、refKey/refUrl/objectKey/overlayText(串)、refKeys/overlayLines(串数组)、cards |
 * | text | text/source/items 等真文案(数组逐元素清洗)、bg/align 等视觉取舍   |
 * | data | headers/rows/series/items 真数值、qr.content/caption           |
 *
 * ★提交闸不再靠"prompt 逐字 = 占位"拦未填(v2 prompt 是真值)——改由 blocks.ts 的
 * `isUntouchedPlaceholder`/示例未替换标记判,拦截降级为友好提示(老板指定不硬拦)。
 */
import type { StyleTokens } from "./canvas";
import type { LongImageBlockForm, LongImageDraft, Primitive, UseCase } from "./blocks";
import {
  IMAGE_PROMPT_PLACEHOLDER,
  STYLE_META,
  USE_CASES,
  WIDE_LAYOUTS,
  defaultPropsFor,
} from "./blocks";
import { parseStyleTokens } from "../../model/style/parseStyleTokens";

/**
 * 模板 payload 版本(独立于 canvas.version)。
 * v2(2026-08-17,老板拍板「套用即所见即所得」):schema **保留 admin 示例内容+示例图**,不再裁空。
 * 前提②「项目未上线、不做迁移不做共存」→ 直接升 2 干净切换;v1 seed 行 parse 返 null(需重生成 seed 为 v2)。
 */
export const TEMPLATE_SCHEMA_V = 2;

export interface LongImageTemplateSchema {
  v: typeof TEMPLATE_SCHEMA_V;
  canvas: {
    direction: "v" | "h";
    width: number;
    scale: 1 | 2 | 3;
    background: "white" | "gray" | "brand";
    useCase: UseCase;
    wideLayout?: "lr" | "tb" | "center" | "three" | null;
    widePresetKey?: string | null;
  };
  style: { key: string; label: string; tone?: string } | null;
  styleTokens?: StyleTokens | null;
  blocks: LongImageBlockForm[];
}

/**
 * 呈现旋钮键(codex R3):inspector 能改、但 defaultPropsFor 里**没有**的视觉取舍
 * (对齐 / 背景 / 行式或卡片 / 缩放 / 数值单位)。只按默认键迭代会把它们整个丢掉 ——
 * 模板作者调的排版一存盘就回默认。标量白名单直通。
 */
const VISUAL_PASSTHROUGH_KEYS = ["align", "bg", "layout", "scale", "unit"] as const;

/** image 示例层的**标量**图字段(v2 保留;平台桶 key/url + 叠字)。字符串才留 —— 挡对象伪装夹带。 */
const IMAGE_STRING_KEYS = ["refKey", "refUrl", "objectKey", "overlayText", "heading"] as const;
/** image 示例层的**字符串数组**字段(v2 保留;photocards 的多图 / 叠字多行)。 */
const IMAGE_STRING_ARRAY_KEYS = ["refKeys", "refUrls", "overlayLines"] as const;

/**
 * ★跨仓契约(P2):image props 里**存平台桶 object key**、api 读模板时必须逐个重签成可访问 URL 的字段。
 * 右画布 image 块显示的是 `refUrl`(单图)/`refUrls`(photocards)—— 它们由 api 从这里列出的 key 字段
 * (`refKey`/`refKeys`)现签回填。api 侧同名集合 = `PosterTemplateService.IMAGE_KEY_TO_URL` 的 keySet,
 * 两侧必须一致(`PosterTemplateImageSignContractTest` 互检)。**在这里加一个图 key 字段,就要同步 api,
 * 否则那张示例图过签名有效期后破图**(pick-style 404 同族,第二次不该再发生)。
 */
export const TEMPLATE_IMAGE_KEY_FIELDS = ["refKey", "refKeys"] as const;

/** 值本身是标量才留(string/number/boolean);对象/数组一律 undefined —— 这是挡"对象伪装夹带 refKey"的那道闸。 */
function keepScalar(v: unknown): string | number | boolean | undefined {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean" ? v : undefined;
}

/**
 * 数组元素的类型清洗(v2:保留内容,但逐元素挡走私):
 *   - 元素是对象 → 只留标量字段(`{label,value,highlight}` 留,`{refKey:…}` 走私被剥);
 *   - 元素是数组(table 的 rows)→ 只留标量单元格;
 *   - 元素是标量(numlist 的字符串条目)→ 原样留。
 */
function sanitizeArrayItem(item: unknown): unknown {
  if (Array.isArray(item)) return item.map((x) => keepScalar(x) ?? "");
  if (item && typeof item === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(item as Record<string, unknown>)) {
      const s = keepScalar(val);
      if (s !== undefined) out[k] = s;
    }
    return out;
  }
  return keepScalar(item) ?? "";
}

/**
 * text / data 原语的内容清洗(v2 定案:**保留 admin 示例内容**,只做类型安全)。
 *
 * ★ v1→v2 的语义反转:v1 把内容标量/数组一律**重置成 defaultPropsFor 占位**(只留条目数/结构),
 *   理由是"素材/文案是租户的"。老板前提①(模板全 admin 上传、无边界)推翻它 —— 示例内容直接进 schema,
 *   套用后中间数据即所见即所得。v2 改为**按类型保留原值**(空/类型不符才回默认),数组逐元素清洗挡走私。
 */
function sanitizeContentProps(
  styleTag: string,
  orig: Record<string, unknown>
): Record<string, unknown> {
  const defaults = defaultPropsFor(styleTag);
  const out: Record<string, unknown> = {};
  for (const [key, defVal] of Object.entries(defaults)) {
    const origVal = orig[key];
    if (Array.isArray(defVal)) {
      // v2:保留原数组内容(逐元素类型清洗);空/非数组才回默认骨架
      out[key] =
        Array.isArray(origVal) && origVal.length > 0 ? origVal.map(sanitizeArrayItem) : defVal;
      continue;
    }
    // 标量(内容 text/source/content/title/sub/caption 或视觉 bg/scale/…):
    // v2 一律按类型保留原值,空字符串或类型不符才回默认。
    const sv = keepScalar(origVal);
    if (
      sv !== undefined &&
      typeof sv === typeof defVal &&
      !(typeof sv === "string" && !sv.trim())
    ) {
      out[key] = sv;
    } else {
      out[key] = defVal;
    }
  }
  // 默认键之外的呈现旋钮:标量直通(数组/对象形状的野值仍然进不来)
  for (const key of VISUAL_PASSTHROUGH_KEYS) {
    if (key in out) continue;
    const v = keepScalar(orig[key]);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

/**
 * image 原语的清洗(v2:保留示例图 + 真 prompt,仍逐键类型安全)。
 * ★ image props 里没有一个键是"原样直通":每个键按期望类型显式构造 —— 对象伪装的 blockRatio/refKey 走私进不来。
 */
function sanitizeImageProps(
  styleTag: string,
  orig: Record<string, unknown>
): Record<string, unknown> {
  const defaults = defaultPropsFor(styleTag);
  const out: Record<string, unknown> = { genMode: orig.genMode === "i2i" ? "i2i" : "t2i" };
  const ratio = orig.blockRatio;
  out.blockRatio =
    typeof ratio === "string" && /^\d+:\d+$/.test(ratio)
      ? ratio
      : String(defaults.blockRatio ?? "3:4");
  // v2:保留真实 prompt(不再逐字回占位);空才回占位(占位仍需逐字 = defaultPropsFor,提交闸靠标记不再靠它)
  out.prompt =
    typeof orig.prompt === "string" && orig.prompt.trim()
      ? orig.prompt
      : String(defaults.prompt ?? IMAGE_PROMPT_PLACEHOLDER);
  // v2:示例图字段(平台桶 key/url + 叠字)—— 字符串才留,挡对象走私
  for (const k of IMAGE_STRING_KEYS) {
    if (typeof orig[k] === "string" && (orig[k] as string).trim()) out[k] = orig[k];
  }
  for (const k of IMAGE_STRING_ARRAY_KEYS) {
    if (Array.isArray(orig[k])) {
      const arr = (orig[k] as unknown[]).filter((x): x is string => typeof x === "string" && !!x);
      if (arr.length) out[k] = arr;
    }
  }
  // photocards:cards 是标量对象数组(逐元素清洗)
  if (Array.isArray(orig.cards) && orig.cards.length > 0) {
    out.cards = (orig.cards as unknown[]).map(sanitizeArrayItem);
  }
  return out;
}

function sanitizeBlockProps(
  primitive: Primitive,
  styleTag: string,
  orig: Record<string, unknown>
): Record<string, unknown> {
  return primitive === "image"
    ? sanitizeImageProps(styleTag, orig)
    : sanitizeContentProps(styleTag, orig);
}

/**
 * admin 存盘入口:LongImageDraft → 骨架版 schema(**程序强制裁剪,不信 admin 自觉**)。
 * styleTag 不在 33 白名单 → 抛 Error(存前本地校验;ai 侧 STYLE_SPECS extra=forbid)。
 */
export function buildTemplateSchema(draft: LongImageDraft): LongImageTemplateSchema {
  if (draft.blocks.length === 0) throw new Error("模板至少需要 1 个区块");
  if (draft.direction === "h") {
    const zones = WIDE_LAYOUTS.find((l) => l.key === draft.wideLayout)?.zones ?? 2;
    if (draft.blocks.length !== zones) {
      // 存盘就拦(codex R3):不拦的话租户套用后在 buildLongImagePayload 才炸,坏的是模板
      throw new Error(
        `横宽版式「${draft.wideLayout}」需要 ${zones} 个分区块,当前 ${draft.blocks.length} 个`
      );
    }
  }
  const blocks = draft.blocks.map((b) => {
    const meta = STYLE_META[b.styleTag];
    if (!meta || meta.primitive !== b.primitive) {
      throw new Error(`区块「${b.name}」的样式 ${b.styleTag} 不在白名单内`);
    }
    return {
      id: b.id,
      primitive: b.primitive,
      styleTag: b.styleTag,
      // name **保留不清洗**(B1 定案:「首屏大图」是骨架的说明,不是内容;codex R9 提议
      // 洗成通用标签,不采纳 —— 名称在模板库/搭建器里对租户明晃晃可见可改,不是隐性夹带,
      // 洗掉反而毁了模板的描述价值;素材/文案那类租户内容才是红线,已在 props 层裁)。
      name: b.name,
      props: sanitizeBlockProps(b.primitive, b.styleTag, b.props),
    };
  });
  const isWide = draft.direction === "h";
  return {
    v: TEMPLATE_SCHEMA_V,
    canvas: {
      direction: draft.direction,
      width: draft.width,
      scale: draft.scale,
      background: draft.background,
      useCase: draft.useCase,
      ...(isWide
        ? { wideLayout: draft.wideLayout, widePresetKey: draft.widePresetKey }
        : { wideLayout: null, widePresetKey: null }),
    },
    style: draft.style
      ? {
          key: draft.style.key,
          label: draft.style.label,
          ...(draft.style.tone ? { tone: draft.style.tone } : {}),
        }
      : null,
    styleTokens: parseStyleTokens(draft.styleTokens),
    blocks,
  };
}

const USE_CASE_KEYS = new Set(USE_CASES.map((u) => u.key));
const WIDE_KEYS = new Set(WIDE_LAYOUTS.map((l) => l.key));
const WIDTH_MIN = 400;
const WIDTH_MAX = 2000;

/**
 * 套用侧验型:DB 里的 schema_json → 干净骨架;形状不对整份丢弃返 null
 * (调用方按「模板坏了」处理,不带半份野值进搭建器)。
 * ★读侧**重新裁一遍** props —— 不信写侧(直连 DB 改过的行、旧版本写的行都可能在)。
 */
export function parseTemplateSchema(raw: unknown): LongImageTemplateSchema | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;
  if (d.v !== TEMPLATE_SCHEMA_V) return null;
  const canvas = d.canvas as Record<string, unknown> | undefined;
  if (!canvas || typeof canvas !== "object") return null;
  if (canvas.direction !== "v" && canvas.direction !== "h") return null;
  if (!USE_CASE_KEYS.has(String(canvas.useCase) as UseCase)) return null;
  const width = Number(canvas.width);
  if (!Number.isFinite(width) || width < WIDTH_MIN || width > WIDTH_MAX) return null;
  const scale = canvas.scale === 1 || canvas.scale === 2 || canvas.scale === 3 ? canvas.scale : 2;
  const background =
    canvas.background === "gray" || canvas.background === "brand" ? canvas.background : "white";
  const isWide = canvas.direction === "h";
  if (isWide && !WIDE_KEYS.has(String(canvas.wideLayout) as "lr")) return null;

  const rawBlocks = d.blocks;
  if (!Array.isArray(rawBlocks) || rawBlocks.length === 0) return null;
  if (isWide) {
    // 读侧镜像存盘闸(codex R4):横宽分区数不匹配的坏行,现在丢比套用后提交时炸好
    const zones = WIDE_LAYOUTS.find((l) => l.key === canvas.wideLayout)?.zones ?? 2;
    if (rawBlocks.length !== zones) return null;
  }
  const blocks: LongImageBlockForm[] = [];
  for (const rb of rawBlocks) {
    if (!rb || typeof rb !== "object") return null;
    const b = rb as Record<string, unknown>;
    const styleTag = String(b.styleTag ?? "");
    const meta = STYLE_META[styleTag];
    if (!meta || meta.primitive !== b.primitive) return null;
    if (!b.id || typeof b.props !== "object" || b.props === null) return null;
    blocks.push({
      id: String(b.id),
      primitive: meta.primitive,
      styleTag,
      name: typeof b.name === "string" && b.name ? b.name : meta.label,
      props: sanitizeBlockProps(meta.primitive, styleTag, b.props as Record<string, unknown>),
    });
  }

  const style = d.style as Record<string, unknown> | null | undefined;
  const styleOut =
    style && typeof style === "object" && typeof style.key === "string" && style.key
      ? {
          key: style.key,
          label: typeof style.label === "string" && style.label ? style.label : style.key,
          ...(typeof style.tone === "string" && style.tone ? { tone: style.tone } : {}),
        }
      : null;

  return {
    v: TEMPLATE_SCHEMA_V,
    canvas: {
      direction: canvas.direction,
      width,
      scale,
      background,
      useCase: String(canvas.useCase) as UseCase,
      ...(isWide
        ? {
            wideLayout: String(canvas.wideLayout) as "lr" | "tb" | "center" | "three",
            widePresetKey:
              typeof canvas.widePresetKey === "string" ? canvas.widePresetKey : "banner",
          }
        : { wideLayout: null, widePresetKey: null }),
    },
    style: styleOut,
    styleTokens: parseStyleTokens(d.styleTokens),
    blocks,
  };
}
